import React, { useState } from 'react';
import { X, Copy, Check, MessageSquare, Users, Film, Sparkles, BookOpen, Heart, Plus, Send, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import type { ConsolidatedQAViewData, CoupleTabData, CoupleData, ReviewerFollowUp, CoupleFollowUp, CoupleQuestion, ClipSuggestionRequest, Reviewer } from '@/types';
import type { ConsolidatedProfileData } from '@/types';
import { CATEGORY_QUESTIONS } from '@/data/category-questions';
import { HIS_SIZE_SCALE, HIS_GIRTH_SCALE, HIS_CUM_SCALE, HIS_CONSISTENCY_SCALE, HIS_TASTE_SCALE, HIS_PRECUM_SCALE, HER_WETNESS_SCALE, HER_SQUIRT_SCALE, HER_SENSITIVITY_SCALE, HER_ORGASM_EASE_SCALE } from '@/data/profile-scales';
import { buildProfileText } from '@/lib/buildProfileText';

interface ConsolidatedQAViewProps {
  data: ConsolidatedQAViewData;
  onClose: () => void;
  onSendReviewerFollowUp: (question: string, parentId?: string) => Promise<void>;
  onSendCoupleFollowUp: (coupleId: string, question: string, parentId?: string) => Promise<void>;
  onSendCoupleQuestionReply: (coupleId: string, parentId: string, replyText: string) => Promise<void>;
  onSendClipSuggestion: (coupleId: string, reviewerId: string, context: string, clips: { url: string; title: string }[]) => Promise<void>;
}

// Discriminated union for active inline form state
type ActiveForm =
  | { type: 'none' }
  | { type: 'askReviewer' }
  | { type: 'replyReviewer'; parentId: string }
  | { type: 'askCouple' }
  | { type: 'replyCoupleFollowUp'; parentId: string }
  | { type: 'replyCoupleQuestion'; parentId: string }
  | { type: 'sendClips' };

// Helper: scale label lookup
function scaleLabel<T extends { readonly value: number; readonly label: string }>(scales: readonly T[], value: number | undefined): string | null {
  if (value === undefined) return null;
  const entry = scales.find(s => s.value === value);
  return entry ? `${entry.label} (${value})` : `${value}`;
}

// Helper: format date
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = status === 'answered' ? 'bg-green-500/20 text-green-400' :
    status === 'queued' ? 'bg-purple-500/20 text-purple-400' :
    'bg-amber-500/20 text-amber-400';
  return <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${colors}`}>{status}</span>;
};

// Section wrapper with sticky header and collapsible content
const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  count?: string;
  headerAction?: React.ReactNode;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, count, headerAction, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="mb-6">
      <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-700 py-2 px-1 mb-3 flex items-center gap-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {icon}
          <h3 className="text-sm font-semibold text-zinc-300">{title}</h3>
          {count && <span className="text-xs text-zinc-500">{count}</span>}
          {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronUp className="w-3.5 h-3.5 text-zinc-500" />}
        </button>
        {headerAction && <div className="ml-auto shrink-0">{headerAction}</div>}
      </div>
      {!collapsed && children}
    </div>
  );
};

// Empty state for a section
const EmptySection: React.FC = () => (
  <p className="text-zinc-600 text-xs italic px-1">No responses yet</p>
);

// --- Inline Forms ---

const InlineQuestionForm: React.FC<{
  placeholder: string;
  onSend: (text: string) => Promise<void>;
  onCancel: () => void;
  buttonLabel?: string;
}> = ({ placeholder, onSend, onCancel, buttonLabel = 'Send' }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setText('');
      onCancel();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-800 p-3 mt-2">
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full bg-zinc-900 text-white text-sm rounded-lg border border-zinc-600 px-3 py-2 placeholder-zinc-500 focus:outline-none focus:border-[#c8e265]/50 resize-none"
      />
      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!text.trim() || sending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#c8e265]/20 text-[#c8e265] border border-[#c8e265]/30 hover:bg-[#c8e265]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Send className="w-3 h-3" />
          {sending ? 'Sending...' : buttonLabel}
        </button>
      </div>
    </div>
  );
};

const InlineSendClipsForm: React.FC<{
  onSend: (context: string, clips: { url: string; title: string }[]) => Promise<void>;
  onCancel: () => void;
}> = ({ onSend, onCancel }) => {
  const [context, setContext] = useState('');
  const [clipItems, setClipItems] = useState<{ url: string; title: string }[]>([]);
  const [newUrl, setNewUrl] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [sending, setSending] = useState(false);

  const isValidUrl = (str: string): boolean => {
    try {
      const url = new URL(str);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch { return false; }
  };

  const addClip = () => {
    if (!newUrl.trim() || !newTitle.trim() || !isValidUrl(newUrl.trim())) return;
    setClipItems(prev => [...prev, { url: newUrl.trim(), title: newTitle.trim() }]);
    setNewUrl('');
    setNewTitle('');
  };

  const removeClip = (index: number) => {
    setClipItems(clipItems.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!context.trim() || clipItems.length === 0 || sending) return;
    setSending(true);
    try {
      await onSend(context.trim(), clipItems);
      onCancel();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-800 p-3 mt-2">
      <textarea
        value={context}
        onChange={e => setContext(e.target.value)}
        placeholder="Context for these clips (e.g. 'Based on your interest in...')"
        rows={2}
        className="w-full bg-zinc-900 text-white text-sm rounded-lg border border-zinc-600 px-3 py-2 placeholder-zinc-500 focus:outline-none focus:border-[#c8e265]/50 resize-none"
      />

      {clipItems.length > 0 && (
        <div className="mt-2 space-y-1">
          {clipItems.map((clip, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-zinc-900 rounded border border-zinc-700">
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{clip.title}</p>
                <p className="text-zinc-500 text-xs truncate">{clip.url}</p>
              </div>
              <button
                onClick={() => removeClip(i)}
                className="text-zinc-500 hover:text-red-400 text-xs shrink-0"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <input
          value={newUrl}
          onChange={e => setNewUrl(e.target.value)}
          placeholder="Clip URL"
          className="flex-1 bg-zinc-900 text-white text-xs rounded border border-zinc-600 px-2 py-1.5 placeholder-zinc-500 focus:outline-none focus:border-[#c8e265]/50"
        />
        <input
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          placeholder="Title"
          className="flex-1 bg-zinc-900 text-white text-xs rounded border border-zinc-600 px-2 py-1.5 placeholder-zinc-500 focus:outline-none focus:border-[#c8e265]/50"
        />
        <button
          onClick={addClip}
          disabled={!newUrl.trim() || !newTitle.trim()}
          className="px-2 py-1.5 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>

      <div className="flex justify-end gap-2 mt-3">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={!context.trim() || clipItems.length === 0 || sending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#c8e265]/20 text-[#c8e265] border border-[#c8e265]/30 hover:bg-[#c8e265]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Send className="w-3 h-3" />
          {sending ? 'Sending...' : `Send ${clipItems.length} Clip${clipItems.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

// --- Tab Bar ---

const CoupleTabBar: React.FC<{
  managedCouple: CoupleTabData | null;
  ownCouple: CoupleTabData | null;
  activeTab: 'managed' | 'own';
  onTabChange: (tab: 'managed' | 'own') => void;
}> = ({ managedCouple, ownCouple, activeTab, onTabChange }) => {
  // Only render if both couples exist with different coupleIds
  if (!managedCouple || !ownCouple) return null;
  if (managedCouple.coupleData.coupleId === ownCouple.coupleData.coupleId) return null;

  return (
    <div className="flex border-b border-zinc-700 shrink-0">
      <button
        onClick={() => onTabChange('managed')}
        className={`flex-1 px-4 py-2.5 text-xs font-medium transition-all border-b-2 ${
          activeTab === 'managed'
            ? 'border-[#c8e265] text-[#c8e265] bg-[#c8e265]/5'
            : 'border-transparent text-zinc-500 hover:text-zinc-300'
        }`}
      >
        Managed: {managedCouple.coupleData.hisName} & {managedCouple.coupleData.herName}
      </button>
      <button
        onClick={() => onTabChange('own')}
        className={`flex-1 px-4 py-2.5 text-xs font-medium transition-all border-b-2 ${
          activeTab === 'own'
            ? 'border-[#c8e265] text-[#c8e265] bg-[#c8e265]/5'
            : 'border-transparent text-zinc-500 hover:text-zinc-300'
        }`}
      >
        Own: {ownCouple.coupleData.hisName} & {ownCouple.coupleData.herName}
      </button>
    </div>
  );
};

// --- Section sub-components ---

const ReviewerQuestionnaireSection: React.FC<{ reviewer: Reviewer }> = ({ reviewer }) => {
  const r = reviewer;
  const fields: [string, string | undefined][] = [
    // Background
    ['Gender', r.gender],
    ['Age Range', r.ageRange],
    ['Relationship', r.relationshipStatus],
    ['Sexually Active', r.sexuallyActive],
    ['Watches Adult Content', r.watchesAdultContent],
    ['Content Themes', r.adultContentThemes],
    ['Content Example', r.adultContentExample],
    ['What Makes Good Content', r.whatMakesGoodContent],
    ['What Feels Fake', r.whatFeelsFake],
    ['Preferred Style', r.preferredStyle],
    ['Familiar With Products', r.familiarWithProducts],
    ['Relevant Experience', r.relevantExperience],
    ['Why Interested', r.whyInterested],
    // Load Boost
    ['Enjoys Volume Content', r.enjoysVolumeContent],
    ['Tried Load Boost', r.triedLoadBoost],
    ['Load Boost Experience', r.loadBoostExperience],
    ['Partner Typical Volume', r.partnerTypicalVolume],
    ['Partner Volume Like/Dislike', r.partnerVolumeLikeDislike],
    ['Want Partner Load Boost', r.wantPartnerLoadBoost],
    ['Enjoys Swallowing', r.enjoysSwallowing],
    ['Swallowing Description', r.swallowingDescription],
    ['Would Enjoy Better Taste', r.wouldEnjoyBetterTaste],
    ['Enjoys Facials', r.enjoysFacials],
    ['Volume Makes Difference', r.volumeMakesDifference],
    // Soaking Wet
    ['Can Squirt', r.canSquirt],
    ['Squirt Scenario', r.squirtScenarioDescription],
    ['Want To Squirt More', r.wantToSquirtMore],
    ['Used Arousal Products', r.usedArousalProducts],
    ['Orgasm From Sex', r.orgasmFromSex],
    ['Typical Wetness', r.typicalWetness !== undefined ? `${r.typicalWetness}/5` : undefined],
    ['Wetness Notes', r.wetnessNotes],
    ['Want To Be Wetter', r.wantToBeWetter],
    ['Enjoys Squirting Content', r.enjoysSquirtingContent],
    ['Want More With Partner', r.wantMoreWithPartner],
    ['Partner Used Arousal Products', r.partnerUsedArousalProducts],
    // Sexual experience
    ['Last Sexual Activity', r.lastSexualActivity],
    ['Facial Experience', r.facialExperience],
    ['Oral Experience', r.oralExperience],
    ['Anal Experience', r.analExperience],
    ['Favorite Sexual Memory', r.favoriteSexualMemory],
    ['Fantasies Not Tried', r.fantasiesNotTried],
    ['Biggest Turn Ons', r.biggestTurnOns],
    ['Deal Breakers', r.dealBreakers],
    ['Additional Notes', r.additionalNotes],
  ];

  const populated = fields.filter(([, v]) => v && v.trim());
  if (populated.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {populated.map(([label, value]) => (
        <div key={label}>
          <p className="text-zinc-500 text-xs">{label}</p>
          <p className="text-white text-sm">{value}</p>
        </div>
      ))}
    </div>
  );
};

// Helper for rendering threaded Q&A items with optional reply button on the last item only
function renderQAItem(
  q: { id?: string; question: string; context?: string; answer?: string; status: string; sentAt: string; answeredAt?: string; answeredBy?: string },
  replies: { id?: string; question: string; answer?: string; answeredAt?: string; answeredBy?: string }[],
  options?: { onReply?: (parentId: string) => void }
) {
  // Reply button only on the very last item in the thread
  const showReplyOnParent = options?.onReply && q.id && q.answer && replies.length === 0;

  return (
    <div className={`rounded-lg p-3 ${
      q.answer ? 'bg-green-500/5 border border-green-500/20' :
      q.status === 'queued' ? 'bg-purple-500/5 border border-purple-500/20 opacity-60' :
      'bg-zinc-800/50 border border-zinc-700'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {q.context && <p className="text-zinc-500 text-xs mb-1">{q.context}</p>}
          <p className="text-zinc-400 text-xs">Q: {q.question}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge status={q.answer ? 'answered' : q.status} />
          {showReplyOnParent && (
            <button
              onClick={() => options.onReply!(q.id!)}
              className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
            >
              Reply
            </button>
          )}
        </div>
      </div>
      {q.answer && (
        <div className="mt-2">
          <p className="text-white text-sm">&ldquo;{q.answer}&rdquo;</p>
          <div className="flex items-center gap-2 mt-1">
            {q.answeredBy && <span className="text-zinc-500 text-xs">by {q.answeredBy}</span>}
            {q.answeredAt && <span className="text-zinc-600 text-xs">{formatDate(q.answeredAt)}</span>}
          </div>
        </div>
      )}
      {!q.answer && q.status === 'pending' && (
        <p className="text-zinc-600 text-xs mt-1">Sent: {formatDate(q.sentAt)} - awaiting response</p>
      )}
      {!q.answer && q.status === 'queued' && (
        <p className="text-purple-400 text-xs mt-1">Queued - not yet sent</p>
      )}
      {/* Threaded replies */}
      {replies.length > 0 && (
        <div className="mt-2 space-y-2">
          {replies.map((reply, i) => {
            const isLast = i === replies.length - 1;
            return (
              <div key={i} className="ml-4 border-l-2 border-blue-500/30 pl-3 py-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-zinc-400 text-xs flex-1">Follow-up: {reply.question}</p>
                  {isLast && options?.onReply && reply.id && (
                    <button
                      onClick={() => options.onReply!(reply.id!)}
                      className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors shrink-0"
                    >
                      Reply
                    </button>
                  )}
                </div>
                {reply.answer && (
                  <div className="mt-1">
                    <p className="text-white text-sm">&ldquo;{reply.answer}&rdquo;</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {reply.answeredBy && <span className="text-zinc-500 text-xs">by {reply.answeredBy}</span>}
                      {reply.answeredAt && <span className="text-zinc-600 text-xs">{formatDate(reply.answeredAt)}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ReviewerCustomQASection: React.FC<{
  reviewerFollowUps: ReviewerFollowUp[];
  onReply?: (parentId: string) => void;
}> = ({ reviewerFollowUps, onReply }) => {
  const items = reviewerFollowUps
    .filter(fu => fu.status !== 'dismissed' && !fu.parentId)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {items.map(q => {
        const replies = reviewerFollowUps.filter(r => r.parentId === q.id);
        return (
          <div key={q.id}>
            {renderQAItem(q, replies, { onReply })}
          </div>
        );
      })}
    </div>
  );
};

const CoupleCategoryQASection: React.FC<{ coupleData: CoupleData }> = ({ coupleData }) => {
  if (!coupleData.categoryQA) return <EmptySection />;

  const entries = Object.entries(coupleData.categoryQA);
  if (entries.length === 0) return <EmptySection />;

  const allAnswers: { categoryId: string; questionId: string; questionText: string; answer: string }[] = [];
  for (const [, response] of entries) {
    for (const [questionId, answer] of Object.entries(response.answers)) {
      if (!answer || !answer.trim()) continue;
      const questionDef = CATEGORY_QUESTIONS.find(q => q.id === questionId);
      allAnswers.push({
        categoryId: questionDef?.categoryId || response.categoryId || '',
        questionId,
        questionText: questionDef?.question || 'Unknown question',
        answer,
      });
    }
  }

  if (allAnswers.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {allAnswers.map(a => (
        <div key={a.questionId} className="rounded-lg p-3 bg-zinc-800/50 border border-zinc-700">
          <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-400 rounded mr-2">{a.categoryId}</span>
          <p className="text-zinc-400 text-xs mt-1">{a.questionText}</p>
          <p className="text-white text-sm mt-1">&ldquo;{a.answer}&rdquo;</p>
        </div>
      ))}
    </div>
  );
};

const CoupleAIFollowUpsSection: React.FC<{
  coupleFollowUps: CoupleFollowUp[];
  onReply?: (parentId: string) => void;
}> = ({ coupleFollowUps, onReply }) => {
  const items = coupleFollowUps
    .filter(fu => fu.status !== 'dismissed' && !fu.parentId)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {items.map(q => {
        const replies = coupleFollowUps.filter(r => r.parentId === q.id);
        return (
          <div key={q.id}>
            {renderQAItem(q, replies, { onReply })}
            {q.reviewerResponse && (
              <div className="ml-4 mt-1 border-l-2 border-lime-500/30 pl-3 py-1">
                <p className="text-lime-400 text-xs">Reviewer response:</p>
                <p className="text-white text-sm">&ldquo;{q.reviewerResponse}&rdquo;</p>
                {q.reviewerResponderName && (
                  <span className="text-zinc-500 text-xs">by {q.reviewerResponderName}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CoupleQuestionsSection: React.FC<{
  coupleQuestions: CoupleQuestion[];
  reviewerId: string;
  onReply?: (parentId: string) => void;
}> = ({ coupleQuestions, reviewerId, onReply }) => {
  const items = coupleQuestions
    .filter(q => q.fromReviewerId === reviewerId && !q.parentId && q.status !== 'dismissed')
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  if (items.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {items.map(q => {
        const replies = coupleQuestions.filter(r => r.parentId === q.id);
        return (
          <div key={q.id}>
            {renderQAItem(q, replies, { onReply })}
          </div>
        );
      })}
    </div>
  );
};

const ClipFeedbackSection: React.FC<{
  clipSuggestions: ClipSuggestionRequest[];
  reviewerId: string;
  reviewerCode: string;
}> = ({ clipSuggestions, reviewerId, reviewerCode }) => {
  const suggestions = clipSuggestions.filter(cs =>
    cs.reviewerId === reviewerId || cs.reviewerId === reviewerCode
  );

  if (suggestions.length === 0) return <EmptySection />;

  return (
    <div className="space-y-2 px-1">
      {suggestions.map(cs => (
        <div key={cs.id} className="rounded-lg p-3 bg-zinc-800/50 border border-zinc-700">
          <p className="text-zinc-500 text-xs mb-2">{cs.context}</p>
          <div className="space-y-1.5">
            {(cs.clips || []).map(clip => (
              <div key={clip.id} className={`flex items-start justify-between gap-2 p-2 rounded ${
                clip.rating ? 'bg-green-500/5 border border-green-500/20' : 'bg-amber-500/5 border border-amber-500/20'
              }`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{clip.title}</p>
                  {clip.rating && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-amber-400 text-xs">{'*'.repeat(clip.rating)}/5</span>
                      {clip.interested !== undefined && (
                        <span className={`text-xs ${clip.interested ? 'text-green-400' : 'text-zinc-500'}`}>
                          {clip.interested ? 'Would try' : 'Not interested'}
                        </span>
                      )}
                    </div>
                  )}
                  {clip.comment && <p className="text-zinc-300 text-xs mt-1">&ldquo;{clip.comment}&rdquo;</p>}
                </div>
                {!clip.rating && (
                  <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded shrink-0">Not reviewed</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Couple profile stats sub-section (with all notes)
const CoupleProfileSection: React.FC<{ coupleData: CoupleData }> = ({ coupleData }) => {
  const his = coupleData.hisProfile;
  const her = coupleData.herProfile;
  if (!his && !her) return <EmptySection />;

  return (
    <div className="space-y-3 px-1">
      {his && (
        <div>
          <p className="text-blue-400 text-xs font-semibold mb-1">His Profile</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {his.size !== undefined && <ProfileStat label="Size" value={scaleLabel(HIS_SIZE_SCALE, his.size)} />}
            {his.girth !== undefined && <ProfileStat label="Girth" value={scaleLabel(HIS_GIRTH_SCALE, his.girth)} />}
            {his.cumIntensity !== undefined && <ProfileStat label="Cum Intensity" value={scaleLabel(HIS_CUM_SCALE, his.cumIntensity)} />}
            {his.cumConsistency !== undefined && <ProfileStat label="Consistency" value={scaleLabel(HIS_CONSISTENCY_SCALE, his.cumConsistency)} />}
            {his.cumTaste !== undefined && <ProfileStat label="Taste" value={scaleLabel(HIS_TASTE_SCALE, his.cumTaste)} />}
            {his.precumProduction !== undefined && <ProfileStat label="Precum" value={scaleLabel(HIS_PRECUM_SCALE, his.precumProduction)} />}
          </div>
          {his.consistencyNotes && <NoteText label="Consistency notes" value={his.consistencyNotes} />}
          {his.tasteNotes && <NoteText label="Taste notes" value={his.tasteNotes} />}
          {his.precumNotes && <NoteText label="Precum notes" value={his.precumNotes} />}
          {his.visualSharingOpen && <NoteText label="Visual sharing" value={his.visualSharingOpen} />}
          {his.visualSharingNotes && <NoteText label="Visual notes" value={his.visualSharingNotes} />}
        </div>
      )}
      {her && (
        <div>
          <p className="text-pink-400 text-xs font-semibold mb-1">Her Profile</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {her.typicalWetness !== undefined && <ProfileStat label="Wetness" value={scaleLabel(HER_WETNESS_SCALE, her.typicalWetness)} />}
            {her.squirtingAbility !== undefined && <ProfileStat label="Squirting" value={scaleLabel(HER_SQUIRT_SCALE, her.squirtingAbility)} />}
            {her.sensitivity !== undefined && <ProfileStat label="Sensitivity" value={scaleLabel(HER_SENSITIVITY_SCALE, her.sensitivity)} />}
            {her.orgasmEase !== undefined && <ProfileStat label="Orgasm Ease" value={scaleLabel(HER_ORGASM_EASE_SCALE, her.orgasmEase)} />}
          </div>
          {her.wetnessNotes && <NoteText label="Wetness notes" value={her.wetnessNotes} />}
          {her.squirtingNotes && <NoteText label="Squirting notes" value={her.squirtingNotes} />}
          {her.sensitivityNotes && <NoteText label="Sensitivity notes" value={her.sensitivityNotes} />}
          {her.orgasmNotes && <NoteText label="Orgasm notes" value={her.orgasmNotes} />}
          {her.visualSharingOpen && <NoteText label="Visual sharing" value={her.visualSharingOpen} />}
          {her.visualSharingNotes && <NoteText label="Visual notes" value={her.visualSharingNotes} />}
        </div>
      )}
    </div>
  );
};

// Couple engagement summary section
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PREF_COLORS: Record<string, string> = {
  love: 'bg-green-500/20 text-green-400',
  like: 'bg-blue-500/20 text-blue-400',
  curious: 'bg-amber-500/20 text-amber-400',
  nope: 'bg-red-500/20 text-red-400',
  neutral: 'bg-zinc-500/20 text-zinc-400',
};

const CoupleEngagementSection: React.FC<{ coupleData: CoupleData }> = ({ coupleData }) => {
  const c = coupleData;
  const hasPrefs = c.preferences && Object.keys(c.preferences).length > 0;
  const hasTried = (c.tried || []).length > 0;
  const hasFavs = (c.favorites || []).length > 0;
  const hasNotes = !!(c.profileNotes || c.generalFeedback);
  const hasFeedback = !!(c.productExcitement || c.wouldSubmitClips);
  const hasDays = c.availableDays && c.availableDays.length > 0;
  const hasFavNotes = c.favoriteNotes && Object.keys(c.favoriteNotes).length > 0;

  const hasAnything = hasPrefs || hasTried || hasFavs || hasNotes || hasFeedback || hasDays;
  if (!hasAnything) return <EmptySection />;

  return (
    <div className="space-y-3 px-1">
      {/* Quick stats row */}
      <div className="flex gap-3 flex-wrap">
        {hasTried && (
          <div className="bg-zinc-800 rounded-lg px-3 py-2 border border-zinc-700">
            <p className="text-zinc-500 text-xs">Tried</p>
            <p className="text-white text-sm font-semibold">{(c.tried || []).length} clips</p>
          </div>
        )}
        {hasFavs && (
          <div className="bg-zinc-800 rounded-lg px-3 py-2 border border-zinc-700">
            <p className="text-zinc-500 text-xs">Favorites</p>
            <p className="text-white text-sm font-semibold">{(c.favorites || []).length}</p>
          </div>
        )}
        {c.productExcitement && (
          <div className="bg-zinc-800 rounded-lg px-3 py-2 border border-zinc-700">
            <p className="text-zinc-500 text-xs">Product Excitement</p>
            <p className="text-amber-400 text-sm font-semibold">{'*'.repeat(c.productExcitement)}/5</p>
          </div>
        )}
        {c.wouldSubmitClips && (
          <div className="bg-zinc-800 rounded-lg px-3 py-2 border border-zinc-700">
            <p className="text-zinc-500 text-xs">Would Submit Clips</p>
            <p className="text-white text-sm font-semibold capitalize">{c.wouldSubmitClips}</p>
          </div>
        )}
      </div>

      {/* Category preferences */}
      {hasPrefs && (
        <div>
          <p className="text-zinc-500 text-xs mb-1.5">Category Preferences</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(c.preferences).map(([catId, pref]) => (
              <span key={catId} className={`text-xs px-1.5 py-0.5 rounded ${PREF_COLORS[pref] || PREF_COLORS.neutral}`}>
                {catId}: {pref}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Available days */}
      {hasDays && (
        <div>
          <p className="text-zinc-500 text-xs mb-1">Available Days</p>
          <div className="flex gap-1">
            {[...c.availableDays!].sort().map(d => (
              <span key={d} className="text-xs px-2 py-0.5 bg-[#c8e265]/20 text-[#c8e265] rounded">
                {DAY_NAMES[d]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Favorite notes */}
      {hasFavNotes && (
        <div>
          <p className="text-zinc-500 text-xs mb-1">Favorite Notes</p>
          <div className="space-y-1">
            {Object.entries(c.favoriteNotes!).map(([clipId, note]) => (
              <div key={clipId} className="rounded p-2 bg-zinc-800/50 border border-zinc-700">
                <span className="text-zinc-500 text-xs">{clipId}:</span>
                <p className="text-white text-xs">{note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile notes & feedback */}
      {c.profileNotes && <NoteText label="Profile Notes" value={c.profileNotes} />}
      {c.generalFeedback && <NoteText label="General Feedback" value={c.generalFeedback} />}
      {c.managedBy && <NoteText label="Managed By" value={c.managedBy} />}
      {c.lastActivityAt && <NoteText label="Last Activity" value={formatDate(c.lastActivityAt)} />}
    </div>
  );
};

const ProfileStat: React.FC<{ label: string; value: string | null }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <span className="text-zinc-500 text-xs">{label}: </span>
      <span className="text-white text-xs">{value}</span>
    </div>
  );
};

const NoteText: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <p className="text-zinc-400 text-xs mt-1"><span className="text-zinc-500">{label}:</span> {value}</p>
);

// --- Main component ---

export const ConsolidatedQAView: React.FC<ConsolidatedQAViewProps> = ({
  data,
  onClose,
  onSendReviewerFollowUp,
  onSendCoupleFollowUp,
  onSendCoupleQuestionReply,
  onSendClipSuggestion,
}) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'managed' | 'own'>('managed');
  const [activeForm, setActiveForm] = useState<ActiveForm>({ type: 'none' });

  // Reset form state when switching tabs
  const handleTabChange = (tab: 'managed' | 'own') => {
    setActiveTab(tab);
    setActiveForm({ type: 'none' });
  };

  const { reviewer, reviewerFollowUps, clips, managedCouple, ownCouple } = data;

  // Determine which tab is showing and resolve the active couple tab data
  const hasBothCouples = !!(managedCouple && ownCouple && managedCouple.coupleData.coupleId !== ownCouple.coupleData.coupleId);
  const activeCoupleTab: CoupleTabData | null = hasBothCouples
    ? (activeTab === 'managed' ? managedCouple : ownCouple)
    : (managedCouple || ownCouple);

  const isOwnTab = hasBothCouples ? activeTab === 'own' : !managedCouple && !!ownCouple;

  // Build ConsolidatedProfileData for copy from active tab
  const handleCopy = async () => {
    try {
      const profileData: ConsolidatedProfileData = {
        reviewer,
        coupleData: activeCoupleTab?.coupleData || null,
        reviewerFollowUps,
        coupleFollowUps: activeCoupleTab?.coupleFollowUps || [],
        coupleQuestions: activeCoupleTab?.coupleQuestions || [],
        clipSuggestions: activeCoupleTab?.clipSuggestions || [],
        clips,
      };
      const text = buildProfileText(profileData);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Section visibility checks
  const hasReviewerBg = !!(reviewer.gender || reviewer.watchesAdultContent);
  const hasCustomQA = reviewerFollowUps.some(fu => fu.status !== 'dismissed' && !fu.parentId);
  const hasCoupleProfile = isOwnTab && !!(activeCoupleTab?.coupleData?.hisProfile || activeCoupleTab?.coupleData?.herProfile);
  const hasCategoryQA = !!(activeCoupleTab?.coupleData?.categoryQA && Object.keys(activeCoupleTab.coupleData.categoryQA).length > 0);
  const hasCoupleFollowUps = (activeCoupleTab?.coupleFollowUps || []).some(fu => fu.status !== 'dismissed' && !fu.parentId);
  const hasCoupleQuestions = (activeCoupleTab?.coupleQuestions || []).some(q => q.fromReviewerId === reviewer.id && !q.parentId && q.status !== 'dismissed');
  const hasClipFeedback = (activeCoupleTab?.clipSuggestions || []).some(cs => cs.reviewerId === reviewer.id || cs.reviewerId === reviewer.code);

  const hasCoupleEngagement = !!(activeCoupleTab?.coupleData && (
    (activeCoupleTab.coupleData.preferences && Object.keys(activeCoupleTab.coupleData.preferences).length > 0) ||
    (activeCoupleTab.coupleData.tried && activeCoupleTab.coupleData.tried.length > 0) ||
    (activeCoupleTab.coupleData.favorites && activeCoupleTab.coupleData.favorites.length > 0) ||
    activeCoupleTab.coupleData.profileNotes ||
    activeCoupleTab.coupleData.generalFeedback ||
    activeCoupleTab.coupleData.productExcitement ||
    activeCoupleTab.coupleData.wouldSubmitClips ||
    (activeCoupleTab.coupleData.availableDays && activeCoupleTab.coupleData.availableDays.length > 0)
  ));

  const hasAnyData = hasReviewerBg || hasCustomQA || hasCoupleProfile || hasCategoryQA || hasCoupleFollowUps || hasCoupleQuestions || hasClipFeedback || hasCoupleEngagement;

  const answeredCustomQA = reviewerFollowUps.filter(fu => fu.reviewerId === reviewer.id && fu.status === 'answered' && !fu.parentId).length;
  const answeredCoupleFollowUps = (activeCoupleTab?.coupleFollowUps || []).filter(fu => fu.status === 'answered' && !fu.parentId).length;
  const answeredCoupleQuestions = (activeCoupleTab?.coupleQuestions || []).filter(q => q.fromReviewerId === reviewer.id && q.status === 'answered' && !q.parentId).length;

  // Subtitle: show active couple info
  const activeCoupleData = activeCoupleTab?.coupleData;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Slide-over panel */}
      <div className="relative w-full max-w-xl bg-zinc-900 border-l border-zinc-700 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-900 shrink-0">
          <div>
            <h2 className="text-white font-semibold text-sm">
              All Q&A: {reviewer.name || reviewer.id}
            </h2>
            {activeCoupleData && (
              <p className="text-pink-400 text-xs">{activeCoupleData.hisName} & {activeCoupleData.herName}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={!hasAnyData}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                copied
                  ? 'bg-lime-500/20 text-lime-400 border border-lime-500/30'
                  : hasAnyData
                    ? 'bg-[#c8e265]/20 text-[#c8e265] border border-[#c8e265]/30 hover:bg-[#c8e265]/30'
                    : 'bg-zinc-800 text-zinc-600 border border-zinc-700 cursor-not-allowed'
              }`}
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Full Profile</>}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab bar (only if both couples exist) */}
        <CoupleTabBar
          managedCouple={managedCouple}
          ownCouple={ownCouple}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {!hasAnyData ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-sm">No Q&A data yet</p>
              <p className="text-zinc-600 text-xs mt-1">Send some questions to get started</p>
            </div>
          ) : (
            <>
              {/* Always: Reviewer Background */}
              {hasReviewerBg && (
                <Section
                  title="Reviewer Background"
                  icon={<BookOpen className="w-4 h-4 text-blue-400" />}
                >
                  <ReviewerQuestionnaireSection reviewer={reviewer} />
                </Section>
              )}

              {/* Always: Reviewer Custom Q&A (with Ask/Reply) */}
              <Section
                title="Reviewer Custom Q&A"
                icon={<MessageSquare className="w-4 h-4 text-amber-400" />}
                count={hasCustomQA ? `(${answeredCustomQA} answered)` : undefined}
                headerAction={
                  <button
                    onClick={() => setActiveForm(activeForm.type === 'askReviewer' ? { type: 'none' } : { type: 'askReviewer' })}
                    className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                  >
                    {activeForm.type === 'askReviewer' ? 'Cancel' : 'Ask Question'}
                  </button>
                }
              >
                {activeForm.type === 'askReviewer' && (
                  <InlineQuestionForm
                    placeholder="Type a question for this reviewer..."
                    onSend={async (text) => {
                      await onSendReviewerFollowUp(text);
                    }}
                    onCancel={() => setActiveForm({ type: 'none' })}
                    buttonLabel="Ask"
                  />
                )}
                {activeForm.type === 'replyReviewer' && (
                  <InlineQuestionForm
                    placeholder="Type your reply..."
                    onSend={async (text) => {
                      await onSendReviewerFollowUp(text, activeForm.parentId);
                    }}
                    onCancel={() => setActiveForm({ type: 'none' })}
                    buttonLabel="Reply"
                  />
                )}
                <ReviewerCustomQASection
                  reviewerFollowUps={reviewerFollowUps}
                  onReply={(parentId) => setActiveForm({ type: 'replyReviewer', parentId })}
                />
              </Section>

              {/* Own tab only: Couple Profile */}
              {isOwnTab && hasCoupleProfile && activeCoupleTab && (
                <Section
                  title="Couple Profile"
                  icon={<Heart className="w-4 h-4 text-pink-400" />}
                >
                  <CoupleProfileSection coupleData={activeCoupleTab.coupleData} />
                </Section>
              )}

              {/* Tab-dependent: Couple Engagement Summary */}
              {hasCoupleEngagement && activeCoupleTab && (
                <Section
                  title="Couple Engagement"
                  icon={<ClipboardList className="w-4 h-4 text-[#c8e265]" />}
                >
                  <CoupleEngagementSection coupleData={activeCoupleTab.coupleData} />
                </Section>
              )}

              {/* Tab-dependent: Couple Category Q&A */}
              {hasCategoryQA && activeCoupleTab && (
                <Section
                  title="Couple Category Q&A"
                  icon={<Users className="w-4 h-4 text-pink-400" />}
                >
                  <CoupleCategoryQASection coupleData={activeCoupleTab.coupleData} />
                </Section>
              )}

              {/* Tab-dependent: Couple AI Follow-ups (with Ask/Reply) */}
              {activeCoupleTab && (
                <Section
                  title="Couple AI Follow-ups"
                  icon={<Sparkles className="w-4 h-4 text-purple-400" />}
                  count={hasCoupleFollowUps ? `(${answeredCoupleFollowUps} answered)` : undefined}
                  headerAction={
                    <button
                      onClick={() => setActiveForm(activeForm.type === 'askCouple' ? { type: 'none' } : { type: 'askCouple' })}
                      className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                    >
                      {activeForm.type === 'askCouple' ? 'Cancel' : 'Ask Question'}
                    </button>
                  }
                >
                  {activeForm.type === 'askCouple' && (
                    <InlineQuestionForm
                      placeholder={`Ask ${activeCoupleTab.coupleData.hisName} & ${activeCoupleTab.coupleData.herName}...`}
                      onSend={async (text) => {
                        await onSendCoupleFollowUp(
                          activeCoupleTab.coupleData.coupleId,
                          text
                        );
                      }}
                      onCancel={() => setActiveForm({ type: 'none' })}
                      buttonLabel="Ask"
                    />
                  )}
                  {activeForm.type === 'replyCoupleFollowUp' && (
                    <InlineQuestionForm
                      placeholder="Type your reply..."
                      onSend={async (text) => {
                        await onSendCoupleFollowUp(
                          activeCoupleTab.coupleData.coupleId,
                          text,
                          activeForm.parentId
                        );
                      }}
                      onCancel={() => setActiveForm({ type: 'none' })}
                      buttonLabel="Reply"
                    />
                  )}
                  <CoupleAIFollowUpsSection
                    coupleFollowUps={activeCoupleTab.coupleFollowUps}
                    onReply={(parentId) => setActiveForm({ type: 'replyCoupleFollowUp', parentId })}
                  />
                </Section>
              )}

              {/* Tab-dependent: Couple Questions from Reviewer (with Reply) */}
              {activeCoupleTab && (
                <Section
                  title="Couple Questions from Reviewer"
                  icon={<MessageSquare className="w-4 h-4 text-green-400" />}
                  count={hasCoupleQuestions ? `(${answeredCoupleQuestions} answered)` : undefined}
                >
                  {activeForm.type === 'replyCoupleQuestion' && (
                    <InlineQuestionForm
                      placeholder="Type your reply..."
                      onSend={async (text) => {
                        await onSendCoupleQuestionReply(
                          activeCoupleTab.coupleData.coupleId,
                          activeForm.parentId,
                          text
                        );
                      }}
                      onCancel={() => setActiveForm({ type: 'none' })}
                      buttonLabel="Reply"
                    />
                  )}
                  <CoupleQuestionsSection
                    coupleQuestions={activeCoupleTab.coupleQuestions}
                    reviewerId={reviewer.id}
                    onReply={(parentId) => setActiveForm({ type: 'replyCoupleQuestion', parentId })}
                  />
                </Section>
              )}

              {/* Tab-dependent: Clip Review Feedback (with Send More Clips) */}
              {activeCoupleTab && (
                <Section
                  title="Clip Review Feedback"
                  icon={<Film className="w-4 h-4 text-pink-400" />}
                  headerAction={
                    <button
                      onClick={() => setActiveForm(activeForm.type === 'sendClips' ? { type: 'none' } : { type: 'sendClips' })}
                      className="text-xs px-2 py-1 rounded bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 transition-colors"
                    >
                      {activeForm.type === 'sendClips' ? 'Cancel' : 'Send More Clips'}
                    </button>
                  }
                >
                  {activeForm.type === 'sendClips' && (
                    <InlineSendClipsForm
                      onSend={async (context, clipsToSend) => {
                        await onSendClipSuggestion(
                          activeCoupleTab.coupleData.coupleId,
                          reviewer.id,
                          context,
                          clipsToSend
                        );
                      }}
                      onCancel={() => setActiveForm({ type: 'none' })}
                    />
                  )}
                  <ClipFeedbackSection
                    clipSuggestions={activeCoupleTab.clipSuggestions}
                    reviewerId={reviewer.id}
                    reviewerCode={reviewer.code}
                  />
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
